<template>
    <!-- Result: just the round and the score -->
    <div
        v-if="variant === 'result'"
        class="compact-match compact-match--result"
        :class="{ 'compact-match--postponed': match.postponed }"
        @click="goToMatch"
    >
        <div class="compact-match__round">
            {{ roundLabel }}
            <span v-if="leagueLabel" class="compact-match__league">{{ leagueLabel }}</span>
        </div>
        <div class="compact-match__result-row">
            <span class="compact-match__logo" :class="teamClass(1)" :title="match.team1.name" @click.stop="goToTeam(match.team1)">
                <img v-if="match.team1.logo" :src="`${assetsUrl}/${match.team1.logo}`" alt="">
                <v-icon v-else color="orange">mdi-basketball</v-icon>
            </span>
            <div v-if="hasScore" class="compact-match__score">
                <span :class="scoreClass(1)">{{ match.scoreTeam1 }}</span>
                <span class="compact-match__sep">:</span>
                <span :class="scoreClass(2)">{{ match.scoreTeam2 }}</span>
            </div>
            <div v-else class="compact-match__time">{{ match.postponed ? 'ΑΝΑΒ.' : '–' }}</div>
            <span class="compact-match__logo" :class="teamClass(2)" :title="match.team2.name" @click.stop="goToTeam(match.team2)">
                <img v-if="match.team2.logo" :src="`${assetsUrl}/${match.team2.logo}`" alt="">
                <v-icon v-else color="orange">mdi-basketball</v-icon>
            </span>
        </div>
    </div>

    <!-- Upcoming / live: date and time, teams, arena -->
    <div
        v-else
        class="compact-match compact-match--upcoming"
        :class="{ 'compact-match--live': live, 'compact-match--postponed': match.postponed }"
        @click="goToMatch"
    >
        <div class="compact-match__top">
            <span v-if="live" class="compact-match__live">
                <span class="compact-match__live-dot" />
                LIVE
            </span>
            <span v-else-if="match.postponed" class="compact-match__postponed">ΑΝΑΒΟΛΗ</span>
            <span v-else class="compact-match__date">
                <v-icon small color="orange">mdi-calendar-outline</v-icon>
                {{ dateLabel }}<template v-if="timeLabel"> · {{ timeLabel }}</template>
            </span>
            <span class="compact-match__meta">
                <span v-if="leagueLabel" class="compact-match__league compact-match__league--chip">{{ leagueLabel }}</span>
                {{ metaLabel }}
            </span>
        </div>

        <div class="compact-match__teams">
            <div class="compact-match__team" @click.stop="goToTeam(match.team1)">
                <img v-if="match.team1.logo" :src="`${assetsUrl}/${match.team1.logo}`" alt="">
                <v-icon v-else large color="orange">mdi-basketball</v-icon>
                <span>{{ match.team1.name }}</span>
            </div>

            <div class="compact-match__center">
                <div v-if="live" class="compact-match__score compact-match__score--live">
                    <span>{{ match.scoreTeam1 || 0 }}</span>
                    <span class="compact-match__sep">:</span>
                    <span>{{ match.scoreTeam2 || 0 }}</span>
                </div>
                <template v-else>
                    <div class="compact-match__kickoff">{{ match.postponed ? '–:–' : (timeLabel || '–:–') }}</div>
                    <span class="compact-match__vs">VS</span>
                </template>
            </div>

            <div class="compact-match__team" @click.stop="goToTeam(match.team2)">
                <img v-if="match.team2.logo" :src="`${assetsUrl}/${match.team2.logo}`" alt="">
                <v-icon v-else large color="orange">mdi-basketball</v-icon>
                <span>{{ match.team2.name }}</span>
            </div>
        </div>

        <div class="compact-match__bottom">
            <span v-if="match.arena" class="compact-match__arena">
                <v-icon x-small color="orange">mdi-basketball-hoop-outline</v-icon>
                {{ match.arena.name }}
            </span>
        </div>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'

    const PHASE_LABELS = {
        regular_season: 'Regular Season',
        placements: 'Προκριματικά',
        playoffs: 'Playoffs'
    }

    // Cards of the horizontal match strips on the team page: a big one for upcoming / live matches, a small one for results
    export default {
        props: {
            match: {
                type: Object,
                required: true
            },
            live: {
                type: Boolean,
                default: false
            },
            variant: {
                type: String,
                default: 'upcoming',
                validator: value => ['upcoming', 'result'].includes(value)
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            hasScore() {
                return !this.match.pending && !this.match.postponed && !(this.match.scoreTeam1 === 0 && this.match.scoreTeam2 === 0)
            },
            dateLabel() {
                if (!this.match.matchDatetime) return 'Χωρίς ημερομηνία'
                return momentTimezone(this.match.matchDatetime).tz('Europe/Athens').locale('el').format('dddd D MMMM')
            },
            timeLabel() {
                return this.match.matchDatetime ? momentTimezone(this.match.matchDatetime).tz('Europe/Athens').format('HH:mm') : ''
            },
            leagueLabel() {
                return this.match.league ? this.match.league.title : ''
            },
            roundLabel() {
                return this.match.customRoundText || (this.match.round ? `Αγωνιστική ${this.match.round}` : '')
            },
            metaLabel() {
                const phase = this.match.cup ? 'Κύπελλο' : (PHASE_LABELS[this.match.phase] || '')
                const round = this.match.customRoundText || (this.match.round ? `Αγ. ${this.match.round}` : '')
                return [round, phase].filter(Boolean).join(' · ')
            }
        },
        methods: {
            winner() {
                if (!this.hasScore || this.match.scoreTeam1 === this.match.scoreTeam2) return 0
                return this.match.scoreTeam1 > this.match.scoreTeam2 ? 1 : 2
            },
            scoreClass(team) {
                const winner = this.winner()
                if (!winner) return ''
                return winner === team ? 'is-winner' : 'is-loser'
            },
            teamClass(team) {
                const winner = this.winner()
                return winner && winner !== team ? 'compact-match__logo--loser' : ''
            },
            goToMatch() {
                if (!this.match.postponed) {
                    this.$router.push({ name: 'Match', params: { id: this.match.id } })
                }
            },
            goToTeam(team) {
                this.$router.push({ name: 'Team', params: { id: team.id } })
            }
        }
    }
</script>

<style scoped>
.compact-match {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    background: #fff;
    border-top: 3px solid #ff7133;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    font-family: Ubuntu, Arial, sans-serif;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.compact-match:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.14);
}

.compact-match--postponed {
    opacity: 0.7;
    cursor: default;
}

/* ---------- Result (small) ---------- */
.compact-match--result {
    gap: 6px;
    width: 210px;
    padding: 8px 10px 10px;
    border-top-color: #0d0d0f;
}

.compact-match__round {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-align: center;
    text-transform: uppercase;
}

.compact-match__result-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
}

.compact-match__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
}

.compact-match__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.compact-match__logo--loser {
    opacity: 0.5;
}

/* ---------- Upcoming (big) ---------- */
.compact-match--upcoming {
    gap: 12px;
    width: 400px;
    padding: 14px 16px;
}

.compact-match--live {
    border-top-color: #ef4444;
}

.compact-match__top {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    font-size: 0.82rem;
}

.compact-match__date {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 800;
    text-transform: capitalize;
}

.compact-match__meta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.compact-match__league {
    display: block;
    margin-top: 1px;
    overflow: hidden;
    color: #e65100;
    font-size: 0.68rem;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.compact-match__league--chip {
    display: inline-block;
    flex: 0 0 auto;
    margin-top: 0;
    padding: 1px 7px;
    border-radius: 4px;
    background: #0d0d0f;
    color: #fff;
}

.compact-match__live,
.compact-match__postponed {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    color: #fff;
    font-weight: 800;
    letter-spacing: 0.06em;
}

.compact-match__live {
    background: #ef4444;
}

.compact-match__postponed {
    background: #616161;
}

.compact-match__live-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fff;
    animation: compact-live 1.2s ease-in-out infinite;
}

@keyframes compact-live {
    50% {
        opacity: 0.25;
    }
}

.compact-match__teams {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 10px;
}

.compact-match__team {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 0;
    text-align: center;
}

.compact-match__team img {
    width: 64px;
    height: 64px;
    object-fit: contain;
}

.compact-match__team span {
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    overflow: hidden;
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.2;
}

.compact-match__center {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.compact-match__kickoff {
    padding: 6px 14px;
    border-radius: 8px;
    background: #f1f1f3;
    font-size: 1.4rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.compact-match__vs {
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.2em;
}

.compact-match__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    min-height: 26px;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.compact-match__arena {
    overflow: hidden;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.78rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.compact-match__bottom ::v-deep .v-btn {
    width: 26px;
    height: 26px;
}

/* ---------- Score (both) ---------- */
.compact-match__score {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    border-radius: 7px;
    background: #0d0d0f;
}

.compact-match__score span {
    min-width: 34px;
    padding: 3px 4px;
    border-radius: 5px;
    color: #fff;
    font-size: 1.1rem;
    font-weight: 800;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.compact-match__score span.is-winner {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
}

.compact-match__score span.is-loser {
    color: rgba(255, 255, 255, 0.55);
}

.compact-match__score .compact-match__sep {
    min-width: 0;
    padding: 0;
    color: rgba(255, 255, 255, 0.4);
}

.compact-match__score--live {
    box-shadow: 0 0 0 2px #ef4444, 0 0 10px rgba(239, 68, 68, 0.5);
}

.compact-match__score--live span {
    font-size: 1.4rem;
}

.compact-match__time {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.8rem;
    font-weight: 800;
}

/* ---------- Phone ---------- */
@media (max-width: 600px) {
    .compact-match--upcoming {
        width: 300px;
    }

    .compact-match__team img {
        width: 52px;
        height: 52px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .compact-match__live-dot {
        animation: none;
    }
}
</style>
